import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  canAbortTrace,
  canRerunTrace,
  canResumeTrace,
  isArchivedTracePreview,
  isDeepActiveProbeTrace,
  toasterApi,
  useTraceAgentLabel,
} from '@terra/ui-design-system';
import type { TracePreviewDto } from '@terra/shared-contracts-trace';
import { terraProxyApiClient } from '../clients/testerClient';
import { useExecutionsStore } from '../store/executionsStore';
import { isNeoOutOfScopeError } from './trace-action-helpers';
import {
  abortTraceExecution,
  rerunTraceExecution,
  resumeTraceExecution,
} from './trace-agent-actions';
import { useNeoOutOfScopeToast } from './useNeoOutOfScopeToast';
import { useTraceBulkActions } from './useTraceBulkActions';

const DEFAULT_AGENT_LABEL = 'Neo';

function useInFlightThreadIds() {
  const [threadIds, setThreadIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );

  const track = useCallback(
    async (threadId: string, run: () => Promise<void>) => {
      setThreadIds((prev) => new Set(prev).add(threadId));

      try {
        await run();
      } finally {
        setThreadIds(
          (prev) => new Set([...prev].filter((id) => id !== threadId)),
        );
      }
    },
    [],
  );

  return { threadIds, track };
}

const describeFailure = ({
  trace,
  error,
  fallback,
}: {
  trace: TracePreviewDto;
  error: unknown;
  fallback: string;
}) =>
  isDeepActiveProbeTrace(trace) && error instanceof Error && error.message
    ? error.message
    : fallback;

export function useTraceActions({
  applicationId,
  onComplete,
}: {
  applicationId: string | undefined;
  onComplete: () => void;
}) {
  const tLifecycle = useTranslations('designSystem.lifecycle');
  const getAgentLabel = useTraceAgentLabel();
  const getNeoOutOfScopeToast = useNeoOutOfScopeToast();
  const removeNeoAttackByThreadId = useExecutionsStore(
    (state) => state.removeNeoAttackByThreadId,
  );
  const bulkActions = useTraceBulkActions({ applicationId, onComplete });
  const { threadIds: resumingTraceIds, track: trackResuming } =
    useInFlightThreadIds();
  const { threadIds: rerunningTraceIds, track: trackRerunning } =
    useInFlightThreadIds();
  const { threadIds: abortingTraceIds, track: trackAborting } =
    useInFlightThreadIds();

  const labelFor = useCallback(
    (trace: TracePreviewDto) =>
      getAgentLabel(trace.name) ?? DEFAULT_AGENT_LABEL,
    [getAgentLabel],
  );

  const rejectArchivedTrace = useCallback(
    (trace: TracePreviewDto) => {
      if (!isArchivedTracePreview(trace)) {
        return false;
      }

      toasterApi.error({
        title: tLifecycle('archivedExecutionActionTooltip'),
      });

      return true;
    },
    [tLifecycle],
  );

  const handleResumeTrace = useCallback(
    async (trace: TracePreviewDto) => {
      if (
        !applicationId ||
        !canResumeTrace(trace) ||
        rejectArchivedTrace(trace)
      )
        return;

      const agentLabel = labelFor(trace);

      await trackResuming(trace.threadId, async () => {
        try {
          await resumeTraceExecution({ trace, applicationId });
          toasterApi.success({
            title: `${agentLabel} resumed`,
            description: `${agentLabel} is resuming.`,
          });
          onComplete();
        } catch (error) {
          console.error(`Failed to resume ${agentLabel}:`, error);
          toasterApi.error({
            title: 'Failed to resume',
            description: describeFailure({
              trace,
              error,
              fallback: `There was an error resuming ${agentLabel}.`,
            }),
          });
        }
      });
    },
    [applicationId, labelFor, onComplete, rejectArchivedTrace, trackResuming],
  );

  const handleRerunTrace = useCallback(
    async (trace: TracePreviewDto) => {
      if (!applicationId || !canRerunTrace(trace) || rejectArchivedTrace(trace))
        return;

      const agentLabel = labelFor(trace);
      const failureToast = {
        title: `Failed to execute ${agentLabel}`,
        description: `There was an error starting ${agentLabel}.`,
      };

      await trackRerunning(trace.threadId, async () => {
        try {
          const result = await rerunTraceExecution({ trace, applicationId });

          if (!result.started) {
            toasterApi.error(
              isNeoOutOfScopeError(result.error)
                ? getNeoOutOfScopeToast()
                : failureToast,
            );

            return;
          }

          toasterApi.success({
            title: `${agentLabel} started`,
            description: `A new ${agentLabel} run has started.`,
          });
          onComplete();
        } catch (error) {
          console.error(`Failed to start ${agentLabel}:`, error);
          toasterApi.error({
            title: failureToast.title,
            description: describeFailure({
              trace,
              error,
              fallback: failureToast.description,
            }),
          });
        }
      });
    },
    [
      applicationId,
      getNeoOutOfScopeToast,
      labelFor,
      onComplete,
      rejectArchivedTrace,
      trackRerunning,
    ],
  );

  const handleDeleteTrace = useCallback(
    async (trace: TracePreviewDto): Promise<boolean> => {
      if (!applicationId || rejectArchivedTrace(trace)) return false;

      await terraProxyApiClient.deleteTrace({
        applicationId,
        threadId: trace.threadId,
      });
      removeNeoAttackByThreadId(trace.threadId);

      onComplete();

      return true;
    },
    [applicationId, onComplete, rejectArchivedTrace, removeNeoAttackByThreadId],
  );

  const handleAbortTrace = useCallback(
    async (trace: TracePreviewDto) => {
      if (!applicationId || !canAbortTrace(trace) || rejectArchivedTrace(trace))
        return;

      const agentLabel = labelFor(trace);

      await trackAborting(trace.threadId, async () => {
        try {
          await abortTraceExecution({ trace, applicationId });
          toasterApi.success({
            title: `${agentLabel} aborted`,
            description: 'The running execution is being stopped.',
          });
          onComplete();
        } catch (error) {
          console.error(`Failed to abort ${agentLabel}:`, error);
          toasterApi.error({
            title: 'Failed to abort',
            description: describeFailure({
              trace,
              error,
              fallback: 'There was an error aborting the running execution.',
            }),
          });
        }
      });
    },
    [applicationId, labelFor, onComplete, rejectArchivedTrace, trackAborting],
  );

  return {
    handleResumeTrace,
    handleRerunTrace,
    handleAbortTrace,
    handleDeleteTrace,
    ...bulkActions,
    resumingTraceIds,
    rerunningTraceIds,
    abortingTraceIds,
  };
}
